/**
 * YardMapCanvas (#3286)
 *
 * The one visual for the parking yard map: the uploaded picture with numbered
 * spot markers at normalized [0, 1] positions. The settings editor and
 * ParkingSpotSelector (PauseWorkDialog, VehicleLocationDialog) both render it,
 * so a marker looks and behaves the same everywhere. It owns no text: labels,
 * instructions and status words come in as props from the caller's namespace.
 *
 * Coordinates are fractions of the picture, so they survive any rendered size;
 * the surface keeps the picture's aspect ratio and scales with its container.
 */

import { useId, useRef, type KeyboardEvent, type MouseEvent, type PointerEvent } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';

export type YardMapMarkerTone = 'free' | 'occupied' | 'neutral';

export interface YardMapMarker {
  id: string;
  identifier: string;
  zoneName: string;
  /** Normalized position, 0..1 of the picture's width / height. */
  x: number;
  y: number;
  tone: YardMapMarkerTone;
  /** Visible status word (colour is never the only signal). */
  statusLabel: string;
  ariaLabel: string;
  selected?: boolean;
  /** Occupied spots are shown but cannot be chosen. */
  disabled?: boolean;
}

/**
 * - `select`: markers are choosable buttons (occupied ones disabled).
 * - `edit`: markers can be dragged or moved with the arrow keys.
 * - `view`: read-only picture with markers.
 */
export type YardMapCanvasMode = 'select' | 'edit' | 'view';

interface YardMapCanvasProps {
  imageUrl: string;
  widthPx: number;
  heightPx: number;
  imageAlt: string;
  groupLabel: string;
  markers: YardMapMarker[];
  mode: YardMapCanvasMode;
  /** Visible keyboard/drag instructions; also the markers' accessible description in edit mode. */
  instructions?: string;
  /** Freezes dragging and keyboard moves (e.g. while a save is pending). */
  disabled?: boolean;
  onSelect?: (id: string) => void;
  onMove?: (id: string, x: number, y: number) => void;
  /** Click on the picture itself (edit mode: place the chosen spot there). */
  onSurfaceClick?: (x: number, y: number) => void;
  onImageError?: () => void;
}

const KEY_STEP = 0.01;
const KEY_STEP_LARGE = 0.05;
const MARKER_SIZE = 44;

const clampUnit = (value: number): number => Math.min(1, Math.max(0, value));

const TONE_COLOR = {
  free: 'success.dark',
  occupied: 'error.dark',
  neutral: 'primary.dark',
} as const;

const ARROW_DELTA: Record<string, readonly [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

export function YardMapCanvas({
  imageUrl,
  widthPx,
  heightPx,
  imageAlt,
  groupLabel,
  markers,
  mode,
  instructions,
  disabled = false,
  onSelect,
  onMove,
  onSurfaceClick,
  onImageError,
}: YardMapCanvasProps) {
  const instructionsId = useId();
  const surfaceRef = useRef<HTMLDivElement>(null);
  // Offset between the grab point and the marker centre, so it does not jump under the pointer.
  const dragRef = useRef<{ id: string; dx: number; dy: number } | null>(null);
  const editable = mode === 'edit' && !disabled;

  /** Pointer position as a fraction of the picture; null before layout. */
  const toFraction = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const rect = surfaceRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return null;
    return { x: (clientX - rect.left) / rect.width, y: (clientY - rect.top) / rect.height };
  };

  const handlePointerDown = (marker: YardMapMarker, event: PointerEvent<HTMLElement>) => {
    if (!editable || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const at = toFraction(event.clientX, event.clientY);
    dragRef.current = { id: marker.id, dx: at ? marker.x - at.x : 0, dy: at ? marker.y - at.y : 0 };
    onSelect?.(marker.id);
  };

  const handlePointerMove = (marker: YardMapMarker, event: PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!editable || !drag || drag.id !== marker.id) return;
    const at = toFraction(event.clientX, event.clientY);
    if (!at) return;
    onMove?.(marker.id, clampUnit(at.x + drag.dx), clampUnit(at.y + drag.dy));
  };

  const endDrag = (event: PointerEvent<HTMLElement>) => {
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
  };

  const handleKeyDown = (marker: YardMapMarker, event: KeyboardEvent<HTMLElement>) => {
    const delta = ARROW_DELTA[event.key];
    if (!editable || !delta) return;
    event.preventDefault();
    const step = event.shiftKey ? KEY_STEP_LARGE : KEY_STEP;
    onMove?.(marker.id, clampUnit(marker.x + delta[0] * step), clampUnit(marker.y + delta[1] * step));
  };

  const handleSurfaceClick = (event: MouseEvent<HTMLElement>) => {
    if (mode !== 'edit' || disabled || !onSurfaceClick) return;
    const at = toFraction(event.clientX, event.clientY);
    if (at) onSurfaceClick(clampUnit(at.x), clampUnit(at.y));
  };

  return (
    <Box role="group" aria-label={groupLabel}>
      {/* Half a marker of padding so markers at the edge are not clipped. */}
      <Box sx={{ px: `${MARKER_SIZE / 2}px`, py: `${MARKER_SIZE / 2}px` }}>
        <Box
          ref={surfaceRef}
          role="presentation"
          onClick={handleSurfaceClick}
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: `${widthPx} / ${heightPx}`,
            bgcolor: 'grey.200',
            borderRadius: 1,
            cursor: mode === 'edit' && onSurfaceClick ? 'crosshair' : 'default',
          }}
        >
          <Box
            component="img"
            src={imageUrl}
            alt={imageAlt}
            draggable={false}
            onError={onImageError}
            sx={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'fill',
              borderRadius: 1,
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
          {markers.map((marker) => {
            const selected = marker.selected === true;
            return (
              <ButtonBase
                key={marker.id}
                disabled={marker.disabled === true || mode === 'view'}
                aria-label={marker.ariaLabel}
                aria-pressed={mode === 'view' ? undefined : selected}
                aria-describedby={mode === 'edit' && instructions ? instructionsId : undefined}
                title={`${marker.identifier} · ${marker.zoneName} · ${marker.statusLabel}`}
                data-marker-id={marker.id}
                onClick={(event) => {
                  event.stopPropagation();
                  if (mode !== 'view') onSelect?.(marker.id);
                }}
                onPointerDown={(event) => handlePointerDown(marker, event)}
                onPointerMove={(event) => handlePointerMove(marker, event)}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onLostPointerCapture={() => {
                  dragRef.current = null;
                }}
                onKeyDown={(event) => handleKeyDown(marker, event)}
                sx={{
                  position: 'absolute',
                  left: `${marker.x * 100}%`,
                  top: `${marker.y * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  zIndex: selected ? 2 : 1,
                  minWidth: MARKER_SIZE,
                  minHeight: MARKER_SIZE,
                  maxWidth: 96,
                  px: 0.5,
                  py: 0.25,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1.5,
                  border: 2,
                  borderColor: 'common.white',
                  bgcolor: TONE_COLOR[marker.tone],
                  color: 'common.white',
                  lineHeight: 1.15,
                  boxShadow: selected
                    ? (theme) => `0 0 0 4px ${theme.palette.warning.main}, 0 1px 4px rgba(0,0,0,0.5)`
                    : '0 1px 4px rgba(0,0,0,0.5)',
                  touchAction: editable ? 'none' : 'auto',
                  cursor: editable ? 'grab' : 'pointer',
                  '&.Mui-disabled': { opacity: 1, cursor: 'not-allowed' },
                  '&.Mui-focusVisible': {
                    outline: '3px solid',
                    outlineColor: 'warning.main',
                    outlineOffset: 3,
                  },
                }}
              >
                <Typography component="span" variant="body2" fontWeight={700} noWrap sx={{ maxWidth: '100%' }}>
                  {marker.identifier}
                </Typography>
                <Typography component="span" noWrap sx={{ maxWidth: '100%', fontSize: '0.65rem', lineHeight: 1.15 }}>
                  {marker.zoneName}
                </Typography>
                <Typography component="span" noWrap sx={{ maxWidth: '100%', fontSize: '0.65rem', lineHeight: 1.15 }}>
                  {marker.statusLabel}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>
      </Box>
      {mode === 'edit' && instructions && (
        <Typography id={instructionsId} variant="caption" color="text.secondary" display="block">
          {instructions}
        </Typography>
      )}
    </Box>
  );
}
